import React from "react";
import { Plus, Trash2, Edit3, Check, X, TrendingUp, Zap, Target, DollarSign } from "lucide-react";
import api from "../../lib/api";

export default function AdminPromo() {
  const [promos, setPromos] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [analytics, setAnalytics] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ code: "", discount_pct: 10, service_slugs: [], max_uses: 0, expires_at: "", note: "" });
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, a] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/services"),
        api.get("/admin/promo-codes/analytics"),
      ]);
      setPromos(p.data);
      setServices(s.data);
      setAnalytics(a.data);
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/promo-codes", {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_pct: Number(form.discount_pct),
        max_uses: Number(form.max_uses || 0),
        expires_at: form.expires_at || null,
      });
      setShowAdd(false);
      setForm({ code: "", discount_pct: 10, service_slugs: [], max_uses: 0, expires_at: "", note: "" });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (p) => {
    await api.patch(`/admin/promo-codes/${p.id}`, { active: !p.active });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce code ?")) return;
    await api.delete(`/admin/promo-codes/${id}`);
    load();
  };

  return (
    <div className="text-text-cream" data-testid="admin-promo-page">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl">Codes promo</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light" data-testid="admin-promo-exclusion-notice">Remises trackables, globales ou par service. Le Shooting de Noël est exclu de tous les codes promo.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="create-promo-btn">
          <Plus size={14} className="mr-2"/> Nouveau code
        </button>
      </div>

      {msg && <div className="mb-4 text-accent-light text-sm">{msg}</div>}

      {/* Analytics mini-dashboard */}
      {analytics && (
        <div className="mb-8" data-testid="promo-analytics">
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              icon={<Target size={18} />}
              label="Codes actifs"
              value={`${analytics.totals.active_codes} / ${analytics.totals.codes_count}`}
            />
            <KpiCard
              icon={<Zap size={18} />}
              label="Utilisations payées"
              value={analytics.totals.total_paid_uses}
            />
            <KpiCard
              icon={<DollarSign size={18} />}
              label="CA généré (CHF)"
              value={analytics.totals.total_revenue_chf.toLocaleString("fr-CH")}
              accent
            />
            <KpiCard
              icon={<TrendingUp size={18} />}
              label="Remises offertes (CHF)"
              value={analytics.totals.total_discount_given_chf.toLocaleString("fr-CH")}
            />
          </div>

          {/* Leaderboard */}
          {analytics.codes.some(c => c.total_attempts > 0) && (
            <div className="bg-white/[0.03] border border-white/10 rounded p-5">
              <div className="eyebrow mb-4">Performance par code</div>
              <div className="space-y-3">
                {analytics.codes
                  .filter(c => c.total_attempts > 0)
                  .slice(0, 5)
                  .map((c, i) => (
                    <LeaderRow key={c.code} rank={i + 1} row={c} max={Math.max(...analytics.codes.map(x => x.revenue_total_chf), 1)} />
                  ))}
              </div>
            </div>
          )}

          {!analytics.codes.some(c => c.total_attempts > 0) && (
            <div className="bg-white/[0.02] border border-dashed border-white/10 rounded p-6 text-center text-text-cream/50 text-sm font-light">
              Aucune utilisation encore. Les stats s'activent dès qu'un client applique un code à la réservation.
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto bg-white/[0.02] border border-white/10 rounded">
        <table className="w-full text-sm" data-testid="promo-table">
          <thead>
            <tr className="text-left text-text-cream/40 border-b border-white/10">
              <th className="p-3 font-light">Code</th>
              <th className="p-3 font-light">Remise</th>
              <th className="p-3 font-light">Services</th>
              <th className="p-3 font-light">Utilisations</th>
              <th className="p-3 font-light">Expire</th>
              <th className="p-3 font-light">Statut</th>
              <th className="p-3 font-light">Note</th>
              <th className="p-3 font-light">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-text-cream/40">Chargement...</td></tr>}
            {!loading && promos.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-text-cream/40">Aucun code.</td></tr>}
            {promos.map(p => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`promo-row-${p.code}`}>
                <td className="p-3 font-serif text-lg text-accent-light">{p.code}</td>
                <td className="p-3">–{p.discount_pct} %</td>
                <td className="p-3 text-xs text-text-cream/60" data-testid={`promo-scope-${p.code}`}>
                  {p.service_slugs?.length ? p.service_slugs.join(", ") : "Tous, hors Shooting de Noël"}
                </td>
                <td className="p-3">{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                <td className="p-3 text-xs">{p.expires_at || "—"}</td>
                <td className="p-3">
                  <span className={`inline-block px-2 py-0.5 text-[0.65rem] uppercase tracking-wider border rounded ${p.active ? "bg-green-500/15 text-green-300 border-green-500/30" : "bg-white/10 text-text-cream/40 border-white/15"}`}>
                    {p.active ? "Actif" : "Inactif"}
                  </span>
                </td>
                <td className="p-3 text-xs text-text-cream/60">{p.note || "—"}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button onClick={() => toggleActive(p)} disabled={p.permanently_disabled} className="text-xs text-text-cream/60 hover:text-accent-light disabled:opacity-50" data-testid={`toggle-${p.code}`}>
                      {p.permanently_disabled ? "Désactivé définitivement" : p.active ? "Désactiver" : "Activer"}
                    </button>
                    <button onClick={() => remove(p.id)} className="text-xs text-red-300/70 hover:text-red-300" data-testid={`del-${p.code}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-dark border border-white/15 rounded p-8 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">Nouveau code promo</h3>
            <div className="space-y-4">
              <div>
                <label className="block eyebrow mb-2">Code *</label>
                <input
                  data-testid="input-code"
                  type="text"
                  value={form.code}
                  onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                  placeholder="BIENVENUE10"
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 uppercase tracking-wider"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block eyebrow mb-2">Remise (%) *</label>
                  <input
                    data-testid="input-pct"
                    type="number"
                    min="1" max="100"
                    value={form.discount_pct}
                    onChange={e => setForm({...form, discount_pct: e.target.value})}
                    className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block eyebrow mb-2">Usages max (0=∞)</label>
                  <input
                    data-testid="input-max"
                    type="number" min="0"
                    value={form.max_uses}
                    onChange={e => setForm({...form, max_uses: e.target.value})}
                    className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block eyebrow mb-2">Expire le (optionnel)</label>
                <input
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm({...form, expires_at: e.target.value})}
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block eyebrow mb-2">Services ciblés (vide = tous, hors Shooting de Noël)</label>
                <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                  {services.filter(s => s.promo_codes_allowed !== false && s.slug !== 'mini-noel-2026').map(s => (
                    <label key={s.slug} className="flex items-center gap-2 text-sm text-text-cream/70 py-1">
                      <input
                        type="checkbox"
                        data-testid={`promo-service-${s.slug}`}
                        checked={form.service_slugs.includes(s.slug)}
                        onChange={e => {
                          if (e.target.checked)
                            setForm({...form, service_slugs: [...form.service_slugs, s.slug]});
                          else
                            setForm({...form, service_slugs: form.service_slugs.filter(x => x !== s.slug)});
                        }}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block eyebrow mb-2">Note interne</label>
                <input
                  type="text"
                  value={form.note}
                  onChange={e => setForm({...form, note: e.target.value})}
                  placeholder="Parrainage Clara, campagne IG..."
                  className="w-full bg-white/5 border border-white/15 rounded px-3 py-2"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="btn-ghost flex-1">Annuler</button>
              <button onClick={create} data-testid="confirm-create-promo" className="btn-primary flex-1" disabled={!form.code || !form.discount_pct}>Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`p-4 rounded border ${accent ? "border-accent-deep/30 bg-accent-deep/5" : "border-white/10 bg-white/[0.03]"}`}>
      <div className="flex items-center gap-2 mb-2 text-text-cream/50">
        <span className={accent ? "text-accent-light" : "text-text-cream/60"}>{icon}</span>
        <span className="eyebrow text-text-cream/40">{label}</span>
      </div>
      <div className={`font-serif text-3xl ${accent ? "text-accent-light" : "text-text-cream"}`}>{value}</div>
    </div>
  );
}

function LeaderRow({ rank, row, max }) {
  const pct = max > 0 ? Math.round((row.revenue_total_chf / max) * 100) : 0;
  const conv = row.conversion_rate;
  const convCls =
    conv >= 60 ? "text-green-300" :
    conv >= 30 ? "text-accent-light" :
    conv > 0 ? "text-yellow-300" : "text-text-cream/40";
  return (
    <div className="flex items-center gap-4" data-testid={`leader-${row.code}`}>
      <div className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-text-cream/50 text-xs font-serif flex-shrink-0">
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between mb-1 gap-3">
          <div className="truncate">
            <span className="font-serif text-lg text-accent-light">{row.code}</span>
            <span className="ml-2 text-xs text-text-cream/40">–{row.discount_pct} %</span>
          </div>
          <div className="font-serif text-xl text-text-cream whitespace-nowrap">CHF {row.revenue_total_chf.toLocaleString("fr-CH")}</div>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-accent-deep to-accent-light rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-[0.7rem] text-text-cream/50">
          <span>{row.paid_count} payé{row.paid_count > 1 ? "s" : ""} / {row.total_attempts} tentatives</span>
          <span className={convCls}>Conversion <strong>{conv}%</strong></span>
          <span>Remises offertes : CHF {row.discount_given_chf.toLocaleString("fr-CH")}</span>
          {row.services_used.length > 0 && (
            <span className="text-text-cream/40">Services : {row.services_used.slice(0, 3).join(", ")}{row.services_used.length > 3 ? "…" : ""}</span>
          )}
        </div>
      </div>
    </div>
  );
}

